// KPI hücre notu — saf arayüz mantığı (portal yetki v2 Dalga 3). Bileşenler ince kalır; burada:
// özet anahtarı/haritası, "yanıt bekleyen" süzgeci, hata → kullanıcı metni, yazar etiketi.
// vitest ile kilitli (kpi-notes-ui.test.ts). Kopya TEK kaynak: kpi-notes-copy.ts.

import type { KpiNoteSummaryCell, KpiValueNoteDto } from "@mahir/shared";
import { KPI_NOTES_COPY } from "./kpi-notes-copy";

export function noteCellKey(c: { metricId: string; entityId: string; year: number }): string {
  return `${c.metricId}|${c.entityId}|${c.year.toString()}`;
}

export function noteSummaryMap(cells: KpiNoteSummaryCell[]): Map<string, KpiNoteSummaryCell> {
  const map = new Map<string, KpiNoteSummaryCell>();
  for (const c of cells) map.set(noteCellKey(c), c);
  return map;
}

/** Danışman Özet "Yanıt bekleyen notlar": son mesajı müşteriden gelen hücreler (en güncel yıl önce). */
export function pendingReplyCells(cells: KpiNoteSummaryCell[]): KpiNoteSummaryCell[] {
  return cells
    .filter((c) => c.count > 0 && c.lastAuthorKind === "client")
    .sort((a, b) => b.year - a.year || b.unread - a.unread);
}

/** Hata → kullanıcı metni: 403 sakin erişim metni, 429 sunucunun Türkçe mesajı, diğer genel metin. */
export function noteErrorMessage(
  err: unknown,
  audience: "portal" | "internal",
  fallback: "sendFailed" | "loadFailed",
): string {
  const C = KPI_NOTES_COPY[audience];
  const status =
    typeof err === "object" && err !== null && "status" in err ? err.status : undefined;
  if (status === 403) return C.forbidden;
  if (status === 429 && err instanceof Error && err.message) return err.message;
  return C[fallback];
}

/** Yeni not kimliği (sunucu `(yazar, clientRequestId)` ile tekilleştirir). */
export function newClientRequestId(): string {
  return crypto.randomUUID();
}

/**
 * Gönderim kimliği tutucusu: yeniden denemede (hata/zaman aşımı) AYNI kimlik taşınır → çift satır
 * olmaz; başarıda ya da metin değişince `reset()` — aksi halde sunucu eski satırı döndürüp yeni
 * metni yutardı.
 */
export class NoteRequestId {
  private id: string | null = null;
  constructor(private readonly make: () => string = newClientRequestId) {}
  current(): string {
    this.id ??= this.make();
    return this.id;
  }
  reset(): void {
    this.id = null;
  }
}

export type NoteTone = "mine" | "consultant" | "client";

/** Mesajın görsel tarafı: portalda danışman yanıtı ayrı çerçeve; kendi mesajı "Siz". */
export function noteTone(note: Pick<KpiValueNoteDto, "authorKind" | "mine">): NoteTone {
  if (note.authorKind === "internal") return "consultant";
  return note.mine ? "mine" : "client";
}

export function noteAuthorLabel(
  note: Pick<KpiValueNoteDto, "authorKind" | "authorName" | "mine">,
  audience: "portal" | "internal",
): string {
  const C = KPI_NOTES_COPY[audience];
  if (note.mine) return C.you;
  if (note.authorName) return note.authorName;
  if (audience === "portal") return KPI_NOTES_COPY.portal.clientFallback;
  return note.authorKind === "internal"
    ? KPI_NOTES_COPY.internal.consultantFallback
    : KPI_NOTES_COPY.internal.clientFallback;
}

/** Grid'e verilen not bağlamı: iki yüz de aynı arayüzü kullanır (KpiGrid yüz bilmez). */
export interface GridNotes {
  audience: "portal" | "internal";
  setId: string;
  /** `noteCellKey` → hücre özeti (görünür yıllar) */
  summary: Map<string, KpiNoteSummaryCell>;
  /** thread açıldı/not yazıldı → özet yeniden çekilir */
  onChanged: () => void;
}
